import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  useBulletinChart,
  useDashboardRecentActivities,
  useDashboardStats,
  useDonasiChart,
} from '@/hooks/useDashboard';
import { User, Gift, BookOpen, Target, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

const MONTH_FILTERS = [
  { value: null, label: 'Semua' },
  { value: 1, label: 'Januari' },
  { value: 2, label: 'Februari' },
  { value: 3, label: 'Maret' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Mei' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' },
  { value: 8, label: 'Agustus' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'Desember' },
] as const;

const formatRelativeTime = (isoDate: string): string => {
  const diff = Date.now() - new Date(isoDate).getTime();
  const seconds = Math.max(1, Math.floor(diff / 1000));

  if (seconds < 60) {
    return `${seconds} detik lalu`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} menit lalu`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} jam lalu`;
  }

  const days = Math.floor(hours / 24);
  return `${days} hari lalu`;
};

const formatCurrency = (value: number): string => `Rp ${value.toLocaleString('id-ID')}`;

export function Dashboard() {
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [donasiChartMonth, setDonasiChartMonth] = useState<number>(selectedMonth ?? new Date().getMonth() + 1);
  const [bulletinChartMonth, setBulletinChartMonth] = useState<number>(selectedMonth ?? new Date().getMonth() + 1);
  const dashboardFilter = { year: currentYear, month: selectedMonth };
  const donasiChartFilter = { year: currentYear, month: donasiChartMonth };
  const bulletinChartFilter = { year: currentYear, month: bulletinChartMonth };

  const { data: stats, isLoading } = useDashboardStats(dashboardFilter);
  const { data: donasiChart } = useDonasiChart(donasiChartFilter);
  const { data: bulletinChart } = useBulletinChart(bulletinChartFilter);
  const { data: recentActivityData, isLoading: isLoadingActivities } = useDashboardRecentActivities(8, dashboardFilter);

  const donasiSeries = donasiChart?.series?.[0]?.data || [];
  const donasiLabels = donasiChart?.labels || [];

  const bulletinSeries = bulletinChart?.series?.[0]?.data || [];
  const bulletinLabels = bulletinChart?.labels || [];

  const donasiChartData = donasiLabels.map((label, index) => ({
    label,
    total: Number(donasiSeries[index] || 0),
  }));

  const bulletinChartData = bulletinLabels.map((label, index) => ({
    label,
    total: Number(bulletinSeries[index] || 0),
  }));

  const activities = recentActivityData?.data || [];
  const donasiChartMonthLabel = MONTH_FILTERS.find((month) => month.value === donasiChartMonth)?.label;
  const bulletinChartMonthLabel = MONTH_FILTERS.find((month) => month.value === bulletinChartMonth)?.label;

  const statCards = [
    {
      title: 'Total Donatur Terdaftar',
      value: stats?.totalDonatur || 0,
      icon: User,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Total Donasi Masuk',
      value: stats?.totalDonasi || 0,
      icon: Gift,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      link: {
        href: 'https://dashboard.midtrans.com/beta/balance',
        label: 'Lihat saldo',
      },
    },
    {
      title: 'Total Pembayaran Donasi',
      value: stats?.totalPembayaran || 0,
      icon: BookOpen,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      isCurrency: true,
    },
    {
      title: 'Total Postingan',
      value: stats?.totalBulletin || 0,
      icon: BookOpen,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Total Program Aktif',
      value: stats?.totalProgram || 0,
      icon: Target,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Beranda</h2>
          <p className="text-muted-foreground">Seluruh data di bawah ditarik langsung dari database.</p>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm font-medium text-muted-foreground">Data bedasarkan bulan</p>
          <Select
            value={selectedMonth === null ? 'all' : String(selectedMonth)}
            onValueChange={(value) => setSelectedMonth(value === 'all' ? null : Number(value))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Pilih bulan" />
            </SelectTrigger>
            <SelectContent>
              {MONTH_FILTERS.map((month) => (
                <SelectItem key={month.label} value={month.value === null ? 'all' : String(month.value)}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
      return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <div className={`${stat.bgColor} rounded-lg p-2`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {isLoading ? '...' : stat.isCurrency ? formatCurrency(stat.value) : stat.value}
                </div>
                {stat.link ? (
                  <a
                    href={stat.link.href}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex text-sm font-medium text-green-700 transition-colors hover:text-green-800"
                  >
                    {stat.link.label}
                  </a>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle>Ringkasan Donasi Harian ({donasiChartMonthLabel} {currentYear})</CardTitle>
            <Select value={String(donasiChartMonth)} onValueChange={(value) => setDonasiChartMonth(Number(value))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Bulan" />
              </SelectTrigger>
              <SelectContent>
                {MONTH_FILTERS.filter((month) => month.value !== null).map((month) => (
                  <SelectItem key={`donasi-${month.label}`} value={String(month.value)}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={donasiChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip formatter={(value) => `Rp ${Number(value).toLocaleString('id-ID')}`} />
                <Line type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle>Distribusi Postingan Harian ({bulletinChartMonthLabel} {currentYear})</CardTitle>
            <Select value={String(bulletinChartMonth)} onValueChange={(value) => setBulletinChartMonth(Number(value))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Bulan" />
              </SelectTrigger>
              <SelectContent>
                {MONTH_FILTERS.filter((month) => month.value !== null).map((month) => (
                  <SelectItem key={`bulletin-${month.label}`} value={String(month.value)}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={bulletinChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="total" fill="#16a34a" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Aktivitas Terbaru</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isLoadingActivities ? (
              <p className="text-sm text-muted-foreground">Memuat aktivitas...</p>
            ) : activities.length === 0 ? (
              <p className="text-sm text-muted-foreground">Belum ada aktivitas terbaru di database.</p>
            ) : (
              activities.map((activity) => (
                <div key={`${activity.feature}-${activity.action}-${activity.time}-${activity.title}`} className="flex items-center gap-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                    {activity.action === 'delete' ? (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    ) : activity.action === 'update' ? (
                      <Info className="h-4 w-4 text-blue-600" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{activity.title}</p>
                    {activity.description ? (
                      <p className="text-xs text-muted-foreground">{activity.description}</p>
                    ) : null}
                  </div>
                  <span className="text-xs text-muted-foreground">{formatRelativeTime(activity.time)}</span>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
